// Production monitoring: captures media load failures, unhandled errors,
// and elevated 4xx/5xx HTTP rates. Escalates to Sentry so alert rules can
// notify immediately. Client-side only; safe no-op during SSR.
import { Sentry } from "@/lib/sentry";

let initialized = false;

// Sliding window HTTP error rate tracking.
type Sample = { t: number; status: number; url: string };
const WINDOW_MS = 60_000;
const MIN_SAMPLES = 20;
const RATE_4XX_ALERT = 0.25; // 25% 4xx over the window
const RATE_5XX_ALERT = 0.05; // 5% 5xx over the window
const ALERT_COOLDOWN_MS = 5 * 60_000;
const samples: Sample[] = [];
let last4xxAlert = 0;
let last5xxAlert = 0;

function prune(now: number) {
  const cutoff = now - WINDOW_MS;
  while (samples.length && samples[0].t < cutoff) samples.shift();
}

function evaluateRates() {
  const now = Date.now();
  prune(now);
  if (samples.length < MIN_SAMPLES) return;
  const total = samples.length;
  const c4 = samples.filter((s) => s.status >= 400 && s.status < 500).length;
  const c5 = samples.filter((s) => s.status >= 500).length;
  const r4 = c4 / total;
  const r5 = c5 / total;

  if (r5 >= RATE_5XX_ALERT && now - last5xxAlert > ALERT_COOLDOWN_MS) {
    last5xxAlert = now;
    Sentry.captureMessage(
      `Elevated 5xx rate: ${(r5 * 100).toFixed(1)}% (${c5}/${total}) in ${WINDOW_MS / 1000}s`,
      {
        level: "error",
        tags: { alert: "http_5xx_rate", severity: "critical" },
        extra: { rate: r5, count5xx: c5, total, windowMs: WINDOW_MS, recent: samples.slice(-10) },
      },
    );
  } else if (r4 >= RATE_4XX_ALERT && now - last4xxAlert > ALERT_COOLDOWN_MS) {
    last4xxAlert = now;
    Sentry.captureMessage(
      `Elevated 4xx rate: ${(r4 * 100).toFixed(1)}% (${c4}/${total}) in ${WINDOW_MS / 1000}s`,
      {
        level: "warning",
        tags: { alert: "http_4xx_rate", severity: "warning" },
        extra: { rate: r4, count4xx: c4, total, windowMs: WINDOW_MS, recent: samples.slice(-10) },
      },
    );
  }
}

function recordHttp(status: number, url: string) {
  samples.push({ t: Date.now(), status, url });
  // Immediate capture of individual server errors (with breadcrumb-style context).
  if (status >= 500) {
    Sentry.addBreadcrumb({
      category: "http",
      level: "error",
      message: `HTTP ${status} ${url}`,
      data: { status, url },
    });
    Sentry.captureMessage(`HTTP ${status} ${url}`, {
      level: "error",
      tags: { http_status: String(status) },
    });
  } else if (status >= 400) {
    Sentry.addBreadcrumb({
      category: "http",
      level: "warning",
      message: `HTTP ${status} ${url}`,
      data: { status, url },
    });
  }
  evaluateRates();
}

function wrapFetch() {
  if (typeof window === "undefined" || !window.fetch) return;
  const orig = window.fetch.bind(window);
  window.fetch = async (...args: Parameters<typeof fetch>) => {
    const req = args[0];
    const url =
      typeof req === "string" ? req : req instanceof URL ? req.toString() : req.url;
    try {
      const res = await orig(...args);
      // Skip Sentry's own ingest URLs and analytics beacons to avoid feedback loops.
      if (!/sentry\.io|ingest\./.test(url)) recordHttp(res.status, url);
      return res;
    } catch (err) {
      Sentry.captureException(err, {
        tags: { alert: "network_failure" },
        extra: { url },
      });
      throw err;
    }
  };
}

function wireMediaErrors() {
  if (typeof window === "undefined") return;
  // Capture failed image/audio/video/script/link loads globally.
  window.addEventListener(
    "error",
    (ev) => {
      const target = ev.target as (HTMLElement & { src?: string; href?: string; currentSrc?: string; tagName?: string }) | null;
      if (!target || target === (window as unknown as HTMLElement)) return;
      const tag = target.tagName?.toLowerCase?.();
      if (!tag) return;
      if (!["img", "audio", "video", "source", "script", "link"].includes(tag)) return;
      const src = target.currentSrc || target.src || target.href || "(unknown)";
      Sentry.captureMessage(`Media load failed: <${tag}> ${src}`, {
        level: tag === "img" ? "warning" : "error",
        tags: { alert: "media_load_failure", media_tag: tag },
        extra: { src, tag },
      });
    },
    true, // capture phase — media errors don't bubble
  );
}

function wireUnhandled() {
  if (typeof window === "undefined") return;
  window.addEventListener("unhandledrejection", (ev) => {
    Sentry.captureException(ev.reason ?? new Error("Unhandled rejection"), {
      tags: { alert: "unhandled_rejection" },
    });
  });
}

export function initMonitoring() {
  if (initialized) return;
  if (typeof window === "undefined") return;
  // Skip the in-editor iframe to save quota (matches sentry.ts).
  if (window.location.hostname.includes("lovableproject.com")) return;
  initialized = true;
  wrapFetch();
  wireMediaErrors();
  wireUnhandled();
}
